import React from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { renderTextField, renderTextArea } from 'components/form/field/redux-field';
import { Field } from 'redux-form';
import { Close as CloseIcon } from '@mui/icons-material';
import { cpStyle } from '../../index.styles';
import { IFields } from '../../table/types';
import { HsiDynamicFieldsEnum } from '../../constants';

interface ISubFormProps {
  formTitle: string;
  fieldsInfo: IFields[];
  setFieldValue: (field: string, value: any) => void;
  updateDynamicPoolList: (poolList: { id: number }[]) => void;
  updateDynamicSureScriptList: (sureScriptList: { id: number }[]) => void;
  updateDynamicPrinterList: (printerList: { id: number }[]) => void;
  sharedEmailErrorMessage: string;
  distributionListEmailErrorMessage: string;
  checkEmailFormat: (email: string | null, filedName: string) => void;
}

export const HealthSystemInformation: React.FC<ISubFormProps> = (
  props: ISubFormProps,
): JSX.Element => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();

  const {
    formTitle,
    fieldsInfo,
    setFieldValue,
    updateDynamicPoolList,
    updateDynamicSureScriptList,
    updateDynamicPrinterList,
    sharedEmailErrorMessage,
    distributionListEmailErrorMessage,
    checkEmailFormat,
  } = props;

  const MAX_LENGTH_INPUT = '100';

  const [dynamicPoolList, setDynamicPoolList] = React.useState<any[]>([]);
  const [dynamicSureScriptList, setDynamicSureScriptList] = React.useState<any[]>([]);
  const [dynamicPrinterList, setDynamicPrinterList] = React.useState<any[]>([]);

  const [sharedEmailValue, setSharedEmailValue] = React.useState<string | null>(null);
  const [distributionListEmailValue, setDistributionListEmailValue] = React.useState<string | null>(
    null,
  );

  const addDynamicPool = () => {
    const newId = dynamicPoolList.length > 0 ? dynamicPoolList.slice(-1)[0].id + 1 : 1;
    setDynamicPoolList((dynamicPoolList: any) => {
      return [
        ...dynamicPoolList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicPool = (id: number) => {
    setDynamicPoolList((dynamicPoolList: any) => dynamicPoolList.filter((x: any) => x.id !== id));
  };

  const addDynamicSureScript = () => {
    const newId = dynamicSureScriptList.length > 0 ? dynamicSureScriptList.slice(-1)[0].id + 1 : 1;
    setDynamicSureScriptList((dynamicSureScriptList: any) => {
      return [
        ...dynamicSureScriptList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicSureScript = (id: number) => {
    setDynamicSureScriptList((dynamicSureScriptList: any) =>
      dynamicSureScriptList.filter((x: any) => x.id !== id),
    );
  };

  const addDynamicPrinter = () => {
    const newId = dynamicPrinterList.length > 0 ? dynamicPrinterList.slice(-1)[0].id + 1 : 1;
    setDynamicPrinterList((dynamicPrinterList: any) => {
      return [
        ...dynamicPrinterList,
        {
          id: newId,
        },
      ];
    });
  };

  const deleteDynamicPrinter = (id: number) => {
    setDynamicPrinterList((dynamicPrinterList: any) =>
      dynamicPrinterList.filter((x: any) => x.id !== id),
    );
  };

  const addDynamicFields = (info: IFields[], fieldName: string) => {
    if (info.length) {
      const foundObject = info.find(t => t.fieldName === fieldName);
      if (foundObject && Object.keys(foundObject).length && foundObject.value !== ' - ') {
        const tempListOfValues = (foundObject.value.split(';') || []).map(t => t.trim());
        if (tempListOfValues.length > 1) {
          const counter = tempListOfValues.length - 1;
          const tempDynamicList = [];
          for (let ind = 0; ind < counter; ind += 1) {
            tempDynamicList.push({
              id: ind + 1,
            });
          }
          if (fieldName === HsiDynamicFieldsEnum.POOL_NAMES) {
            setDynamicPoolList(tempDynamicList);
          }
          if (fieldName === HsiDynamicFieldsEnum.SURESCRIPTS_NAME) {
            setDynamicSureScriptList(tempDynamicList);
          }
          if (fieldName === HsiDynamicFieldsEnum.PRINTER) {
            setDynamicPrinterList(tempDynamicList);
          }
        }
      }
    }
  };

  React.useEffect(() => {
    if (fieldsInfo.length) {
      addDynamicFields(fieldsInfo, HsiDynamicFieldsEnum.POOL_NAMES);
      addDynamicFields(fieldsInfo, HsiDynamicFieldsEnum.SURESCRIPTS_NAME);
      addDynamicFields(fieldsInfo, HsiDynamicFieldsEnum.PRINTER);
    }
  }, [fieldsInfo]);

  React.useEffect(() => {
    const tempLoopFields = Object.values(HsiDynamicFieldsEnum) as string[];
    if (fieldsInfo.length) {
      for (const fieldInfo of fieldsInfo) {
        if (
          fieldInfo.fieldName &&
          !tempLoopFields.includes(fieldInfo.fieldName) &&
          fieldInfo.value !== ' - '
        ) {
          setFieldValue(fieldInfo.fieldName, fieldInfo.value);
        } else if (fieldInfo.fieldName && tempLoopFields.includes(fieldInfo.fieldName)) {
          if (fieldInfo.value !== ' - ') {
            const tempListOfValues = (fieldInfo.value.split(';') || []).map(t => t.trim());
            tempListOfValues.forEach((val, index) => {
              setFieldValue(`${fieldInfo.fieldName}_${index}`, val);
            });
          }
        }
      }
    }
  }, [fieldsInfo]);

  React.useEffect(() => {
    updateDynamicPoolList(dynamicPoolList);
  }, [dynamicPoolList]);

  React.useEffect(() => {
    updateDynamicSureScriptList(dynamicSureScriptList);
  }, [dynamicSureScriptList]);

  React.useEffect(() => {
    updateDynamicPrinterList(dynamicPrinterList);
  }, [dynamicPrinterList]);

  React.useEffect(() => {
    checkEmailFormat(sharedEmailValue, 'hsi_shared_email');
  }, [sharedEmailValue]);

  React.useEffect(() => {
    checkEmailFormat(distributionListEmailValue, 'hsi_distribution_list_email');
  }, [distributionListEmailValue]);

  return (
    <Grid item xs={12}>
      <Accordion className={classes.accordionheader}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIconColor} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>
            <Tooltip title={formTitle} placement="right-end">
              <span className={classes.accordionTitle}>{`${formTitle}`}</span>
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.AccordionDetailsForm}>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={3}>
              <Field
                name="hsi_dba"
                label="DBA:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_emr"
                label="EMR:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_dispensing_system"
                label="Dispensing System:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_wholesaler"
                label="Wholesaler:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_pos_system"
                label="POS System:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_medicaid_enrollment_unit"
                label="Medicaid Enrollment Unit:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_state_medicaid_number"
                label="State Medicaid Number:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_medicaid_submitter_id"
                label="Medicaid Submitter ID:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_340b_id"
                label="340B ID:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_cost_center"
                label="Cost Center:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_shared_email"
                label="Shared Email:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
                onBlur={(v: any) => {
                  setSharedEmailValue(
                    v?.target?.value && v.target.value.length ? v.target.value : null,
                  );
                }}
                meta={{ touched: !!sharedEmailErrorMessage, error: sharedEmailErrorMessage }}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name="hsi_distribution_list_email"
                label="Distribution List Email:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
                onBlur={(v: any) => {
                  setDistributionListEmailValue(
                    v?.target?.value && v.target.value.length ? v.target.value : null,
                  );
                }}
                meta={{
                  touched: !!distributionListEmailErrorMessage,
                  error: distributionListEmailErrorMessage,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container className={classes.dynamicButtons}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Pool</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicPool()} variant="outlined">
                  <span>+ POOL</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={5}>
              <Field
                name="hsi_pool_names_0"
                label="Pool Names:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <Field
                name="hsi_pool_clinic_reference_0"
                label="Clinic Reference:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicPoolList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_pool_names_${t.id}`}
                      label="Pool Names:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_pool_clinic_reference_${t.id}`}
                      label="Clinic Reference:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.centerIcon}
                    key={`hsi_pool_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicPool(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container className={classes.dynamicButtons}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Surescripts</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicSureScript()} variant="outlined">
                  <span>+ SURESCRIPTS</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={5}>
              <Field
                name="hsi_sure_script_name_0"
                label="SureScripts Name:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <Field
                name="hsi_sure_script_clinic_reference_0"
                label="Clinic Reference:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicSureScriptList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_sure_script_name_${t.id}`}
                      label="SureScripts Name:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_sure_script_clinic_reference_${t.id}`}
                      label="Clinic Reference:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.centerIcon}
                    key={`hsi_sure_script_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicSureScript(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container className={classes.dynamicButtons}>
            <Grid item xs={6} className={classes.subSectionTitle}>
              <Typography>
                <span>Printers</span>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Grid container justifyContent="flex-end" className={classes.dynamicButtons}>
                <Button onClick={() => addDynamicPrinter()} variant="outlined">
                  <span>+ PRINTER</span>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3} className={classes.rowMarginTop}>
            <Grid item xs={5}>
              <Field
                name="hsi_printer_0"
                label="Printers:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
            <Grid item xs={5}>
              <Field
                name="hsi_printer_clinic_reference_0"
                label="Clinic Reference:"
                component={renderTextField}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
          {(dynamicPrinterList || []).map((t: any) => {
            if (t.id > 0) {
              return (
                <Grid container spacing={3} className={classes.rowMarginTop}>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_printer_${t.id}`}
                      label="Printers:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Field
                      name={`hsi_printer_clinic_reference_${t.id}`}
                      label="Clinic Reference:"
                      component={renderTextField}
                      validate={[]}
                      width="100%"
                      cpInputProperties={{
                        maxLength: MAX_LENGTH_INPUT,
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.centerIcon}
                    key={`hsi_printer_delete_icon_${t.id}`}
                    onClick={() => {
                      deleteDynamicPrinter(t.id);
                    }}
                    width="100%"
                  >
                    <CloseIcon />
                  </Grid>
                </Grid>
              );
            }
            return '';
          })}
          <Grid item xs={12} className={classes.dividerLine}>
            <Divider />
          </Grid>
          <Grid container xs={12} className={classes.rowMarginTop2}>
            <Grid item xs={12}>
              <Field
                name="hsi_additional_information"
                label=""
                component={renderTextArea}
                placeholder="Additional Information"
                rowsNumber={2}
                validate={[]}
                width="100%"
                cpInputProperties={{
                  maxLength: MAX_LENGTH_INPUT,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
